import { useNavigate, Link } from "react-router-dom";
import { Building2, CalendarRange, Users2, LayoutDashboard, ArrowRight, ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";

/**
 * Org Admin Dashboard — an ORG-LEVEL page, deliberately mounted OUTSIDE the
 * requireProduction wrapper in App.js so it's reachable both from the
 * Productions picker (no show selected yet) and from inside a production
 * via the sidebar footer. Because we sit outside <Layout>, we render our
 * own header/back-link so the page still feels chromed.
 *
 * The 4 cards are launchpads — the real Season Planner UI ships as a
 * follow-up, so its card is badged "Phase 2".
 */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const cards = [
    {
      to: "/admin/seasons",
      icon: CalendarRange,
      title: "Season Planner",
      body: "Group shows into seasons, block out dates, and move a show between seasons in one click.",
      cta: "Manage seasons",
    },
    { to: "/facilities", icon: Building2, title: "Facilities & Rooms",
      body: "Own the venue side — buildings, rooms, cross-show room schedule.", cta: "Open Facilities" },
    { to: "/users", icon: Users2, title: "People",
      body: "Invite, edit, and remove company members. Assign roles.", cta: "Open People",
      requiresProduction: true },
    { to: "/platform/overview", icon: LayoutDashboard, title: "Platform",
      body: "Super Admin only. Cross-tenant users, modules, and legal.",
      cta: "Open Platform", superOnly: true },
  ];

  const filtered = cards.filter((c) => !c.superOnly || user?.role === "super_admin");

  return (
    <div className="min-h-screen bg-black text-white">
      <ImpersonationBanner />
      <header className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <button
            data-testid="admin-dashboard-back"
            onClick={() => nav("/productions")}
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Back to shows
          </button>
          <div className="text-right hidden md:block">
            <div className="text-sm text-white">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user?.role}</div>
          </div>
          <button
            data-testid="admin-dashboard-logout"
            onClick={async () => { await logout(); nav("/login"); }}
            className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8 animate-fade-up" data-testid="admin-dashboard">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Org Admin</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Admin Dashboard</h1>
          <p className="mt-1 text-zinc-400 text-sm max-w-2xl">
            Everything you own at the organization level — one page, no hunting. Facilities and season configuration live here so
            they never get in the way when you&apos;re mid-rehearsal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(({ to, icon: Icon, title, body, cta, soon, requiresProduction }) => (
            <Link
              key={to}
              to={to}
              data-testid={`admin-card-${to.replace(/\W/g, "-")}`}
              className="group rounded-lg border border-white/10 bg-[#0F0F0F] p-5 hover:border-gold/40 hover:bg-[#141414] transition-colors relative"
            >
              {soon && (
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.22em] text-gold/70 border border-gold/30 rounded px-1.5 py-0.5">
                  Phase 2
                </span>
              )}
              {requiresProduction && (
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.22em] text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">
                  Per show
                </span>
              )}
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-gold/10 border border-gold/30 p-2 text-gold shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-white">{title}</div>
                  <p className="text-sm text-zinc-400 mt-1">{body}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-gold group-hover:translate-x-0.5 transition-transform">
                    {cta} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
